import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import Home from './pages/Home';
import ServicePage from './pages/ServicePage';
import About from './pages/About';
import Contact from './pages/Contact';
import FAQ from './pages/FAQ';
import TrustpilotPage from './pages/TrustpilotPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Error404 from './pages/Error404';
import Error403 from './pages/Error403';

// Metadata Map with titles (max 512 px) and descriptions (max 970 px)
const METADATA_MAP = {
  '/': {
    title: 'Glarmester Egedal, Nordsjælland & store dele af Sjælland | Energirude Eksperten',
    description: 'Glarmester i Egedal, Nordsjælland og store dele af Sjælland. Specialister i energiruder og rudeskift. Med base i Smørum tilbyder vi gratis rådgivning og besøg.',
    ogImage: 'https://assets.auxodev.dk/assets/8d0f5bf2-9099-44a5-baa5-a4be578e55f8/energirude-eksperten-glarmestre-firmabil.jpg'
  },
  '/energiruder': {
    title: 'Energiruder i Egedal, Nordsjælland & store dele af Sjælland | Glarmester',
    description: 'Få nye energiruder og spar på varmen i Egedal, Nordsjælland og store dele af Sjælland. Med base i Smørum yder vi professionel rådgivning og gratis besøg.'
  },
  '/udskiftning-af-ruder': {
    title: 'Udskiftning af ruder i Egedal, Nordsjælland & store dele af Sjælland | Glarmester',
    description: 'Trænger dine ruder til udskiftning? Vi skifter punkterede ruder i Egedal, Nordsjælland og store dele af Sjælland. Base i Smørum. Book et gratis besøg i dag.',
    ogImage: 'https://assets.auxodev.dk/assets/8d0f5bf2-9099-44a5-baa5-a4be578e55f8/energirude-eksperten-glarmestre-firmabil.jpg'
  },
  '/termoruder': {
    title: 'Termoruder i Egedal, Nordsjælland & store dele af Sjælland | Glarmester',
    description: 'Punkteret termorude eller koldtræk? Vi monterer nye termoruder i Egedal, Nordsjælland og store dele af Sjælland. Med base i Smørum tilbyder vi gratis opmåling.'
  },
  '/glasloesninger': {
    title: 'Glasløsninger i Egedal, Nordsjælland & store dele af Sjælland | Glarmester',
    description: 'Professionelle glasløsninger til bolig og erhverv i Egedal, Nordsjælland og store dele af Sjælland. Med base i Smørum rådgiver vi om specialglas og montering.'
  },
  '/gratis-besoeg': {
    title: 'Book gratis besøg i Egedal, Nordsjælland & store dele af Sjælland | Glarmester',
    description: 'Book et gratis besøg og få rådgivning om nye ruder i Egedal, Nordsjælland og store dele af Sjælland. Med base i Smørum gennemgår vi vinduer og besparelser.'
  },
  '/om-os': {
    title: 'Om os | Glarmester i Egedal, Nordsjælland & store dele af Sjælland',
    description: 'Lær Energirude Eksperten at kende. Din glarmester i Egedal, Nordsjælland og store dele af Sjælland med base i Smørum. Kvalitetsarbejde til faste priser.'
  },
  '/kontakt': {
    title: 'Kontakt Energirude Eksperten | Egedal, Nordsjælland & store dele af Sjælland',
    description: 'Kontakt Energirude Eksperten for et uforpligtende tilbud i Egedal, Nordsjælland og store dele af Sjælland. Vi har base i Smørum og svarer hurtigt. Tlf: 21 82 80 00.'
  },
  '/trustpilot': {
    title: 'Trustpilot anmeldelser | Egedal, Nordsjælland & store dele af Sjælland',
    description: 'Læs hvad vores kunder i Egedal, Nordsjælland og store dele af Sjælland siger om Energirude Eksperten. Med base i Smørum yder vi høj kvalitet og tryghed.'
  },
  '/faq': {
    title: 'FAQ - Energirude Eksperten | Egedal, Nordsjælland & store dele af Sjælland',
    description: 'Få svar om energiruder, termoruder og glarmesterarbejde i Egedal, Nordsjælland og store dele af Sjælland. Med base i Smørum rådgiver vi altid gerne.'
  },
  '/privatlivspolitik': {
    title: 'Privatlivspolitik | Energirude Eksperten',
    description: 'Læs privatlivspolitik for Energirude Eksperten. Vi passer godt på dine data i Egedal, Nordsjælland og store dele af Sjælland, med base i Smørum.'
  },
  '/403': {
    title: '403 Adgang nægtet | Energirude Eksperten',
    description: 'Adgang nægtet. Du har ikke tilladelse til denne ressource hos Energirude Eksperten i Egedal, Nordsjælland og store dele af Sjælland med base i Smørum.'
  },
  '/404': {
    title: '404 Ikke fundet | Energirude Eksperten',
    description: 'Siden blev ikke fundet. Find information om energiruder hos Energirude Eksperten i Egedal, Nordsjælland og store dele af Sjælland med base i Smørum.'
  }
};

// Scroll Restoration and Metadata Manager Component
function PageManager() {
  const location = useLocation();

  useEffect(() => {
    // Scroll to top on page change
    window.scrollTo(0, 0);

    // Update document title and meta description dynamically
    const meta = METADATA_MAP[location.pathname] || METADATA_MAP['/404'];
    if (meta) {
      document.title = meta.title;
      
      // Update description tag
      let metaDescTag = document.querySelector('meta[name="description"]');
      if (!metaDescTag) {
        metaDescTag = document.createElement('meta');
        metaDescTag.setAttribute('name', 'description');
        document.head.appendChild(metaDescTag);
      }
      metaDescTag.setAttribute('content', meta.description);

      // Update OpenGraph tags
      let ogTitleTag = document.querySelector('meta[property="og:title"]');
      if (!ogTitleTag) {
        ogTitleTag = document.createElement('meta');
        ogTitleTag.setAttribute('property', 'og:title');
        document.head.appendChild(ogTitleTag);
      }
      ogTitleTag.setAttribute('content', meta.title);

      let ogDescTag = document.querySelector('meta[property="og:description"]');
      if (!ogDescTag) {
        ogDescTag = document.createElement('meta');
        ogDescTag.setAttribute('property', 'og:description');
        document.head.appendChild(ogDescTag);
      }
      ogDescTag.setAttribute('content', meta.description);

      // Update OpenGraph Image tag
      let ogImageTag = document.querySelector('meta[property="og:image"]');
      if (!ogImageTag) {
        ogImageTag = document.createElement('meta');
        ogImageTag.setAttribute('property', 'og:image');
        document.head.appendChild(ogImageTag);
      }
      if (meta.ogImage !== undefined) {
        ogImageTag.setAttribute('content', meta.ogImage);
      } else {
        ogImageTag.setAttribute('content', 'https://assets.auxodev.dk/assets/8d0f5bf2-9099-44a5-baa5-a4be578e55f8/energirude-eksperten-glarmestre-firmabil.jpg');
      }
    }
  }, [location.pathname]);

  return null;
}

function App() {
  return (
    <>
      <PageManager />
      <div className="flex flex-col min-h-screen bg-white text-brand-dark antialiased overflow-x-clip">
        <Header />
        
        <main className="flex-grow">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/energiruder" element={<ServicePage />} />
            <Route path="/udskiftning-af-ruder" element={<ServicePage />} />
            <Route path="/termoruder" element={<ServicePage />} />
            <Route path="/glasloesninger" element={<ServicePage />} />
            <Route path="/gratis-besoeg" element={<ServicePage />} />
            <Route path="/om-os" element={<About />} />
            <Route path="/kontakt" element={<Contact />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/trustpilot" element={<TrustpilotPage />} />
            <Route path="/privatlivspolitik" element={<PrivacyPolicy />} />
            
            {/* Error Routes */}
            <Route path="/403" element={<Error403 />} />
            <Route path="*" element={<Error404 />} />
          </Routes>
        </main>

        <Footer />
      </div>
    </>
  );
}

export default App;
